/**
 * The particularity with Monoprix's basket-sync is 
 * 1. They can't develop any healper method for us so we have to call their API directly
 * 2. We never manages to make their cart update when we call their API so we have to reload the page for it to update
 * 
 * This means that we must wait until after the page reloads to check the cart content after pushing products to their cart
 * 
 * This will be a BIG problem if we want to upgrade Monoprix to a version of SDK > 9.0 or to SSR.
 */

////////////////////////////////////////////////////
///////////////////METHODS TO CALL//////////////////
////////////////////////////////////////////////////

export function resetOnEmptyBasket() {
  let listenerWasAttached = false;
  let emptyButtonIsOnPage = false;
  const observer = new MutationObserver(() =>{
    const button = document.querySelector('[data-synthetics=basket-confirm-empty]');
    if (emptyButtonIsOnPage && !button) {
      emptyButtonIsOnPage = false;
      listenerWasAttached = false;
    }
    else {
      if (!listenerWasAttached && button) {
        emptyButtonIsOnPage = true;
        button.addEventListener('click', () => window.mealz.basket.reset());
        listenerWasAttached = true;
      }
    }
  });
  observer.observe(document.body, { attributes: false, childList: true, subtree: true });
}

export function handlePayment() {
  if (hasPayment()) {
    window.mealz.basketSync.handlePayment(paymentTotal());
  }
}

export function listenToRetailerBasket() {
  // Override window.dataLayer.push to detect changes on retailer basket
  window.dataLayer.push = function (p) {
    return function (object) {
      // In case us fetching the basket before updating dataLayer breaks their process,
      // We udpate it before fetching
      const call = p.call(window.dataLayer, object);
      // Each time Monoprix pushes in the dataLayer, if the action affect the basket,
      // fetch the basket and call the callback
      if (object.eventAction?.includes('BASKET')) {
        window.mealz.basketSync.retailerBasketChanged(getBasketProducts());
      }
      return call;
    };
  }(window.dataLayer.push);
  // Send the starting retailer basket
  window.mealz.basketSync.retailerBasketChanged(getBasketProducts());
}

export function pushProductsToBasket(retailerProducts) {
  const getProductIdRequests = [];
  // Get all products id (we have their retailerId)
  retailerProducts.forEach(p => {
    const req = new XMLHttpRequest();
    req.open('GET', `/api/webproductpagews/v5/products/bop?retailerProductId=${p.id}`, true);
    req.onreadystatechange = function () {
      // If all queries are ready (i.e. when the last request passes here) map the good id to each product, then push them
      if (checkIfAllRequestsReady(getProductIdRequests.map(q => q.request))) {
        pushProducts(processQueries(getProductIdRequests));
      }
    };
    getProductIdRequests.push({request: req, quantity: p.quantity})
  })
  // We send all requests after setting them all up to be sure to not fall in the case where
  // for some reason all requests that were sent are ready before the other are sent
  getProductIdRequests.forEach(query => {
    query.request.send()
  })
}

// Get basket content and return it as an array of {id, quantity}

// We need to make two API requests here:
// 1. To /api/v5/products/basket for detailed product information
// 2. To /api/cart/v1/carts/active/cart-view for current cart contents
// This is because we need to combine data from both endpoints:
// - Product details (like retailerProductId and price) from the first request
// - Cart-specific information (like quantity and productId) from the second request
// By matching productIds between these two responses, we get all the data we need

export function getBasketProducts() {
  const products = [];
  const req1 = new XMLHttpRequest();
  const req2 = new XMLHttpRequest();

  req1.open('GET', `/api/v5/products/basket`, false);
  req1.send();

  req2.open('GET', `/api/cart/v1/carts/active/cart-view?productGroupingType=CATEGORIES`, false);
  req2.send();

  if (req1.status === 200 && req2.status === 200) {
    const response1 = JSON.parse(req1.response);
    const response2 = JSON.parse(req2.response);

    if (response1.entities && response1.entities.product && response2.items) {
      response2.items.forEach(item => {
        const k = item.productId;
        if (response1.entities.product[k]) {
          products.push({
            id: response1.entities.product[k].retailerProductId,
            quantity: item.quantity,
            productId: k,
            price: parseFloat(response1.entities.product[k].price.current.amount)
          });
        }
      });
    }
  }

  return products;
}

///////////////////////////////////////////////
/////////////////USEFUL METHODS////////////////
///////////////////////////////////////////////

function hasPayment() {
  return localStorage.getItem('miam-payment-total') !== null;
}

function paymentTotal() {
  const total = localStorage.getItem('miam-payment-total');
  localStorage.removeItem('miam-payment-total');
  return total;
}

/**
 * Utility method: returns true if all requests are ready, return false if at least 1 isn't
 * @param {XMLHttpRequest[]} requests
 * @returns
 */
 function checkIfAllRequestsReady(requests) {
  let finished = true;
  requests.forEach(function (request) {
    if (request.readyState != 4) {
      finished = false;
    }
  });
  return finished;
}

/**
 * Compare the quantity of items in successful queries to the quantities of items in Monopirx's basket
 * @param {XMLHttpRequest[]} getProductIdRequests
 * @returns an array of {id , quantity} with the right id of all products extracted from the queries responses
 */
function processQueries(getProductIdRequests) {
  const products = []
  getProductIdRequests.forEach(query => {
    if (query.request.status == 200) {
      const id = JSON.parse(query.request.responseText)?.product?.productId;
      // Search the product in Monoprix's basket
      products.push({productId: id, quantity: query.quantity});
    }
  });
  return products;
}

/**
 * Add & remove products to Monoprix' basket
 * @param {{id: string, quantity: number}} retailerProducts
 */
function pushProducts(retailerProducts) {
  const pushRequests = [];
  retailerProducts.forEach((p, i) => {
    const req = new XMLHttpRequest();
    if (p.quantity >= 0) {
      req.open('POST', `/api/cart/v1/carts/active/add-items`, true)
    }
    else {
      // The remove requests takes a positive number for the quantity to remove
      p.quantity = -p.quantity;
      req.open('POST', `/api/cart/v1/carts/active/remove-items`, true)
    }
    req.setRequestHeader("Content-Type", "application/json");
    req.setRequestHeader("x-csrf-token", window.__INITIAL_STATE__.session.csrf.token);
    req.onreadystatechange = function () {
      if (checkIfAllRequestsReady(pushRequests)) {
        // TODO: SHOULD UPDATE MONOPRIX'S BASKET (NEED THEIR METHOD)
      }
    };
    setTimeout(() => req.send(JSON.stringify([p])), 100*i + 1);
    pushRequests.push(req);
    // if we are on the scan-list page, wait after the last product request is sent and go to the after scan page
    if (window.location.href.includes('ma-liste') && i === retailerProducts.length - 1) {
      setTimeout(() => location.assign('/content/ma-liste-merci'), 100*retailerProducts.length + 1)
    }
  })
}
